import {
  type GetApiAdminCompaniesResponse,
  getProgress,
  queryKeyFn,
  useGetApiAdminIntacctCompanyInfo,
  useGetApiAdminJobStatus,
  usePostApiAdminCompanyTreeSettingsBulkDelete,
} from '@sit/client-shared';
import { useForm } from '@tanstack/react-form';
import { skipToken, useQueryClient } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import { CardColumn, CardRow } from 'carbon-react/lib/components/card';
import Dialog from 'carbon-react/lib/components/dialog';
import Divider from 'carbon-react/lib/components/divider';
import Form from 'carbon-react/lib/components/form';
import Loader from 'carbon-react/lib/components/loader/__next__';
import Message from 'carbon-react/lib/components/message';
import Textbox from 'carbon-react/lib/components/textbox';
import Text from 'carbon-react/lib/components/typography';
import { useCallback, useState } from 'react';
import { ProgressBar } from '../../components/ProgressBar';
import toastr from '../../helpers/toastr';

interface DeleteCompanyForm {
  intacctCompanyId: string;
}

function DeleteInstanceForm() {
  const queryClient = useQueryClient();
  const form = useForm({
    defaultValues: {
      intacctCompanyId: '',
    },
    onSubmit: async ({ value }) => {
      if (confirmationState === 'waiting') {
        setConfirmationState('selected');
      } else if (confirmationState === 'selected') {
        setConfirmationState('confirming');
      } else if (confirmationState === 'confirming') {
        await scheduleDeleteCompany(value);
      }
    },
  });

  const intacctCompanyId = form.state.values.intacctCompanyId || '';

  const [confirmationState, setConfirmationState] = useState<
    'waiting' | 'selected' | 'confirming'
  >('waiting');
  const [jobId, setJobId] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [intervalMs, setIntervalMs] = useState<number | false>(2500);

  const mutation = usePostApiAdminCompanyTreeSettingsBulkDelete();

  const { data: companyData, isFetching } = useGetApiAdminIntacctCompanyInfo(
    {
      pathParams: {
        intacctCompanyId,
      },
    },
    {
      enabled: confirmationState === 'selected' && intacctCompanyId !== '',
      retry: (failureCount, error) => {
        setConfirmationState('waiting');
        return error.status !== 404;
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const resetCompanyCache = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeyFn({
        path: '/admin/intacct-company-info/{intacctCompanyId}',
        operationId: 'getApiAdminIntacctCompanyInfo',
        variables: {
          // @ts-expect-error -- intentional
          pathParams: {},
        },
      }),
    });
  }, [queryClient]);

  const resetForm = () => {
    setConfirmationState('waiting');
    form.reset();
    resetCompanyCache();
  };

  const onSuccess = () => {
    resetForm();
    toastr.success(
      `You've successfully deleted ${intacctCompanyId}'s Sage Intelligent Time account.`,
    );
    setJobId(undefined);
    setIntervalMs(false);
    queryClient.setQueryData(
      queryKeyFn({
        path: '/admin/companies',
        operationId: 'getApiAdminCompanies',
        variables: {},
      }),
      (oldData: GetApiAdminCompaniesResponse | undefined) => {
        if (!oldData) {
          return oldData;
        }
        return oldData.filter(
          (company) => company.externalId !== intacctCompanyId,
        );
      },
    );
  };

  const { data: jobStatusResponse } = useGetApiAdminJobStatus(
    jobId
      ? {
          queryParams: {
            jobId,
            queueName: 'delete-company-tree',
          },
          requestConfig: {
            skipToast: true,
          },
        }
      : skipToken,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: intervalMs,
      retry: (failureCount, error) => {
        if (error?.status === 404) {
          onSuccess();
          return false;
        }
        if (!(error instanceof Error)) {
          return false;
        }
        if (error.message?.includes('Too many requests') || failureCount < 5) {
          return true;
        }
        setErrorMessage(error.message);
        return false;
      },
    },
  );

  const progress = jobStatusResponse
    ? getProgress(jobStatusResponse?.progress)
    : 0;
  const approximateCompletionTime =
    jobStatusResponse?.estimatedCompletionTimestamp
      ? new Date(jobStatusResponse.estimatedCompletionTimestamp)
      : undefined;

  const scheduleDeleteCompany = async (data: DeleteCompanyForm) => {
    const companyTreeSettingsId = companyData?.companyTreeSettingsId;
    if (!companyTreeSettingsId) {
      toastr.error('Company tree settings ID is missing.');
      return;
    }
    await mutation.mutateAsync(
      {
        body: {
          companyTreeSettingsIds: [companyTreeSettingsId],
        },
      },
      {
        onSuccess: (result) => {
          if (result.success) {
            toastr.success(
              `${data.intacctCompanyId} is scheduled for deletion.`,
            );
            setJobId(result.jobId);
            setConfirmationState('waiting');
          } else {
            toastr.error(
              `The ${data.intacctCompanyId} company deletion was unsuccessful, retry in a few minutes.`,
            );
          }
        },
        onError: (error) => {
          toastr.error(
            error instanceof Error ? error.message : (error as string),
          );
        },
      },
    );
  };

  const saveButton =
    confirmationState === 'waiting' || isFetching ? (
      <Button
        onClick={() => void form.handleSubmit()}
        disabled={isFetching || form.state.isSubmitting}
      >
        {form.state.isSubmitting ? (
          <Loader
            loaderType="ring"
            variant="inline"
            size="small"
            loaderLabel="Searching..."
          />
        ) : (
          'Search'
        )}
      </Button>
    ) : (
      <Button
        variant="destructive"
        onClick={() => void form.handleSubmit()}
        disabled={form.state.isSubmitting}
      >
        {form.state.isSubmitting ? (
          <Loader
            loaderType="ring"
            variant="inline"
            size="small"
            loaderLabel="Deleting..."
          />
        ) : (
          'Delete'
        )}
      </Button>
    );

  return (
    <Form
      errorCount={form.state.errors.length}
      saveButton={
        <CardRow>
          <CardColumn>{saveButton}</CardColumn>
        </CardRow>
      }
      leftSideButtons={
        <CardRow>
          <CardColumn>
            <Button variantType="secondary" onClick={resetForm}>
              Clear
            </Button>
          </CardColumn>
        </CardRow>
      }
    >
      <form.Field
        name="intacctCompanyId"
        validators={{
          onChange: ({ value }) => {
            return !value ? 'This field is required' : undefined;
          },
        }}
      >
        {(field) => (
          <Textbox
            name={field.name}
            value={field.state.value}
            onBlur={field.handleBlur}
            onChange={(ev) => {
              field.handleChange(ev.target.value);
              setConfirmationState('waiting');
            }}
            label="Intacct company ID"
            required
            error={field.state.meta.errors[0]}
          />
        )}
      </form.Field>
      {companyData && (
        <>
          <Divider type="horizontal" variant="typical" />
          <Message>
            <Text>Intacct company ID: {companyData.intacctCompanyId}</Text>
            <Text>Company tree ID: {companyData.companyTreeSettingsId}</Text>
            <Text>Tenant type: {companyData.tenantType || 'Not set'}</Text>
            {companyData.admins.length > 0 ? (
              companyData.admins.map((admin, index) => (
                <Text key={admin.username}>
                  Admin {index + 1}: {admin.username} ({admin.email})
                </Text>
              ))
            ) : (
              <Text>No admins</Text>
            )}
          </Message>
        </>
      )}
      {jobId != null && (
        <>
          <ProgressBar
            now={progress}
            approximateCompletionTime={approximateCompletionTime}
          />
          {!!errorMessage && (
            <Box mt="10px">
              <Message variant="error">{errorMessage}</Message>
            </Box>
          )}
        </>
      )}
      <Dialog
        onCancel={resetForm}
        title="Delete company instance"
        open={confirmationState === 'confirming'}
      >
        <Box flexDirection="row" justifyContent="flex-end">
          Are you sure you want to delete{' '}
          <strong>{companyData?.intacctCompanyId ?? intacctCompanyId}</strong>?
          This cannot be undone.
          <Divider type="horizontal" variant="typical" />
          <div className="intacct-admin-modal-footer">
            <Button onClick={resetForm}>Cancel</Button>
            <Button
              variantType="secondary"
              variant="destructive"
              onClick={() => void form.handleSubmit()}
              ml={2}
            >
              Delete
            </Button>
          </div>
        </Box>
      </Dialog>
    </Form>
  );
}

export default DeleteInstanceForm;
